import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { AuthService } from '../../../services/auth.service';

type Step = 'form' | 'verify';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit {
  private readonly auth = inject(AuthService);

  firstName = '';
  lastName = '';
  email = '';
  password = '';
  acceptTerms = false;

  readonly codeDigits = signal<string[]>(['', '', '', '', '', '']);
  readonly verificationCode = computed(() => this.codeDigits().join(''));

  recaptchaToken: string | null = null;

  readonly step = signal<Step>('form');
  readonly showPassword = signal(false);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly countdown = signal(0);

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {}

  onCaptchaResolved(token: string | null): void {
    this.recaptchaToken = token;
  }

  togglePassword(): void { this.showPassword.update((v) => !v); }

  onSubmit(): void {
    this.error.set(null);
    if (!this.acceptTerms || !this.email || !this.password || !this.firstName) return;

    if (!this.recaptchaToken) {
      this.error.set('Completa la verificación reCAPTCHA.');
      return;
    }

    this.loading.set(true);
    this.auth.sendVerificationCode(this.email, this.recaptchaToken).subscribe({
      next: () => {
        this.loading.set(false);
        this.step.set('verify');
        this.startCountdown(600);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message ?? 'No se pudo enviar el código. Intenta de nuevo.');
        this.recaptchaToken = null;
      }
    });
  }

  onVerify(): void {
    this.error.set(null);
    if (this.verificationCode().length < 6) return;
    this.loading.set(true);
    this.auth.register(this.firstName, this.lastName, this.email, this.password, this.verificationCode()).subscribe({
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message ?? 'Código incorrecto o expirado.');
      },
      complete: () => this.loading.set(false)
    });
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, '').slice(-1);
    this.codeDigits.update((digits) => { const d = [...digits]; d[index] = val; return d; });
    if (val && index < 5) (document.getElementById(`digit-${index + 1}`) as HTMLInputElement)?.focus();
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.codeDigits()[index] && index > 0) {
      (document.getElementById(`digit-${index - 1}`) as HTMLInputElement)?.focus();
    }
  }

  onDigitPaste(event: ClipboardEvent): void {
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6).split('');
    if (digits.length === 6) { event.preventDefault(); this.codeDigits.set([...digits]); }
  }

  resendCode(): void {
    if (this.countdown() > 0 || !this.recaptchaToken) return;
    this.error.set(null);
    this.loading.set(true);
    this.auth.sendVerificationCode(this.email, this.recaptchaToken).subscribe({
      next: () => { this.loading.set(false); this.startCountdown(600); },
      error: (err) => { this.loading.set(false); this.error.set(err?.error?.message ?? 'Error al reenviar.'); }
    });
  }

  goBack(): void {
    this.step.set('form');
    this.codeDigits.set(['', '', '', '', '', '']);
    this.recaptchaToken = null;
    this.error.set(null);
    this.stopCountdown();
  }

  get countdownLabel(): string {
    const s = this.countdown();
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  }

  private startCountdown(seconds: number): void {
    this.stopCountdown();
    this.countdown.set(seconds);
    this.countdownInterval = setInterval(() => {
      this.countdown.update((v) => (v > 0 ? v - 1 : 0));
      if (this.countdown() === 0) this.stopCountdown();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
  }

  noopSocial(event: Event): void { event.preventDefault(); }
}
